import { type ActorRefFrom, type MachineImplementationsFrom, setup } from 'xstate';

import { Actions } from './setup/sweetpass-checkout.machine.actions';
import { Actors } from './setup/sweetpass-checkout.machine.actors';
import { generateInitialContext } from './setup/sweetpass-checkout.machine.context';
import { Guards } from './setup/sweetpass-checkout.machine.guards';
import * as Inputs from './setup/sweetpass-checkout.machine.input';
import { type Context, type Event, type Input } from './setup/sweetpass-checkout.machine.types';
import { getPaymentErrorMessage, toErrorMessage } from './setup/sweetpass-checkout.machine.utils';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Event,
  },
  actions: Actions,
  actors: Actors,
  guards: Guards,
});

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Loads the payment setup and confirms the sweetpass payment with Stripe.
 *
 * A confirmed payment is not a completed purchase: the backend fulfills it
 * asynchronously, so confirmed (and resumed) payments are handed off to the
 * payment state polling machine through `onPaymentConfirmed` and `onPendingPaymentFound`.
 */
export const sweetpassCheckoutMachine = model.createMachine({
  id: 'sweetpass-checkout',
  initial: 'loading',
  context: (args) => generateInitialContext(args.input),
  on: {
    SET_CONFIRM_STRIPE: { actions: ['setConfirmStripe'] },
    STRIPE_FAILED_TO_LOAD: { actions: ['setStripeFailedToLoad'] },
  },
  states: {
    loading: {
      invoke: {
        id: 'fetchPaymentInfo',
        src: 'fetchPaymentInfo',
        onDone: {
          target: 'checkingPendingPayment',
          actions: [{ type: 'assignPaymentInfo', params: (args) => args.event.output }],
        },
        onError: {
          target: 'failedToLoad',
          actions: [
            { type: 'onFailedToLoad', params: (args) => ({ errorMessage: toErrorMessage(args.event.error) }) }, // prettier-ignore
          ],
        },
      },
    },
    failedToLoad: {},
    checkingPendingPayment: {
      invoke: {
        id: 'fetchPendingPayment',
        src: 'fetchPendingPayment',
        onDone: [
          {
            target: 'idle',
            guard: { type: 'checkIfHasPendingPayment', params: (args) => args.event.output },
            actions: [{ type: 'onPendingPaymentFound', params: (args) => args.event.output }],
          },
          { target: 'idle' },
        ],
        onError: { target: 'idle' },
      },
    },
    idle: {
      on: {
        PURCHASE: 'purchasing',
      },
    },
    purchasing: {
      invoke: {
        id: 'confirmPayment',
        src: 'confirmPayment',
        input: Inputs.getConfirmPaymentInput,
        onDone: [
          {
            target: 'idle',
            guard: { type: 'checkIfPaymentConfirmed', params: (args) => args.event.output },
            actions: [
              'incrementPurchaseAttempt',
              { type: 'onPaymentConfirmed', params: (args) => args.event.output },
            ],
          },
          {
            target: 'idle',
            guard: { type: 'checkIfAlreadyPurchased', params: (args) => args.event.output },
            actions: ['onAlreadyPurchased'],
          },
          {
            target: 'idle',
            guard: { type: 'checkIfPaymentCanceled', params: (args) => args.event.output },
            actions: ['clearPendingPayment', 'incrementPurchaseAttempt', 'onPaymentCanceled'],
          },
          {
            target: 'idle',
            actions: [
              'clearPendingPayment',
              'incrementPurchaseAttempt',
              {
                type: 'onPaymentFailed',
                params: (args) => ({ errorMessage: getPaymentErrorMessage(args.event.output) }),
              },
            ],
          },
        ],
        onError: {
          target: 'idle',
          actions: [
            'clearPendingPayment',
            'incrementPurchaseAttempt',
            { type: 'onPaymentFailed', params: (args) => ({ errorMessage: toErrorMessage(args.event.error) }) }, // prettier-ignore
          ],
        },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type SweetpassCheckoutMachineActorRef = ActorRefFrom<typeof sweetpassCheckoutMachine>;
export type SweetpassCheckoutMachineImplementations = MachineImplementationsFrom<typeof sweetpassCheckoutMachine>; // prettier-ignore
